import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserAddOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';
import { parseCustomerNameLocalesRoot, resolveCustomerDisplayName } from '../constants/commerceNameLocales';
import CommerceCustomerTablePickerModal from '../components/CommerceCustomerTablePickerModal';
import PasswordFieldWithGenerate from '../components/PasswordFieldWithGenerate';
import { CommerceWpaWelcomeWhatsappPanel, CommerceWpaWelcomeEmailPanel } from '../components/CommerceWpaWelcomeSettingsPanels';
import RichTextEditor from '../components/WorkflowDesigner/components/RichTextEditor';
import { buildCommerceAuditColumns, useCommerceUserTimezone } from '../utils/commerceListDateUtils';
import {
  PASSWORD_POLICY_SCOPES,
  generatePasswordForScope,
  usePasswordPolicy,
} from '../utils/passwordPolicyUtils';

const { Title, Text } = Typography;

const NoOpFormControl = () => null;

const CommerceWpaPermissionPage = () => {
  const { t, currentLanguage } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const { policy: wpaPasswordPolicy, generating: wpaPasswordGenerating } = usePasswordPolicy(
    PASSWORD_POLICY_SCOPES.wpaAccount,
    currentLanguage
  );
  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);

  const [activeTab, setActiveTab] = useState('roles');

  // Roles state
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleForm] = Form.useForm();
  const [otpTplForm] = Form.useForm();

  // Groups state
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupForm] = Form.useForm();
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [groupCustomers, setGroupCustomers] = useState([]);
  const [groupCustomersLoading, setGroupCustomersLoading] = useState(false);
  const [groupDetailTab, setGroupDetailTab] = useState('members');
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [memberSearchText, setMemberSearchText] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [memberSearchSelectedIds, setMemberSearchSelectedIds] = useState([]);
  const [groupMemberSelectedRowKeys, setGroupMemberSelectedRowKeys] = useState([]);
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [expandedGroupCustomerPickerInitialKeys, setExpandedGroupCustomerPickerInitialKeys] = useState([]);
  const [groupEditCustomers, setGroupEditCustomers] = useState([]);
  const [groupEditCustomersLoading, setGroupEditCustomersLoading] = useState(false);
  const [groupEditCustomerPickerOpen, setGroupEditCustomerPickerOpen] = useState(false);
  const [groupEditCustomerPickerMode, setGroupEditCustomerPickerMode] = useState('customers');
  const [groupEditPickerInitialKeys, setGroupEditPickerInitialKeys] = useState([]);
  /** 每次開啟客戶選擇器就 +1，強制重新掛載該對話框元件，確保不會殘留上一次的搜尋條件 */
  const [groupEditCustomerPickerNonce, setGroupEditCustomerPickerNonce] = useState(0);
  const [addCustomerPickerNonce, setAddCustomerPickerNonce] = useState(0);
  const [groupEditDefaultCustomer, setGroupEditDefaultCustomer] = useState(null);

  // Accounts state（PWA 帳號設置；2026-05 起與 contact_lists 解耦）
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountPage, setAccountPage] = useState(1);
  const [accountPageSize, setAccountPageSize] = useState(20);
  const [accountTotal, setAccountTotal] = useState(0);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  /** 編輯中的 account（包含後端回來的詳細資料）；null 表示新建模式 */
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountForm] = Form.useForm();
  const watchedAccountCustomerIds = Form.useWatch('customerIds', accountForm);
  const watchedAccountDefaultCustomerId = Form.useWatch('defaultCustomerId', accountForm);
  const [accountCustomerLabelRows, setAccountCustomerLabelRows] = useState([]);
  const [accountCustomerPickerOpen, setAccountCustomerPickerOpen] = useState(false);
  const [accountPickerInitialKeys, setAccountPickerInitialKeys] = useState([]);
  const [accountDefaultCustomerPickerOpen, setAccountDefaultCustomerPickerOpen] = useState(false);
  const [accountDefaultPickerInitialKeys, setAccountDefaultPickerInitialKeys] = useState([]);
  /** 每次開啟客戶選擇器就 +1，強制重新掛載該對話框元件，確保不會殘留上一次的搜尋條件 */
  const [accountCustomerPickerNonce, setAccountCustomerPickerNonce] = useState(0);
  const [accountDefaultPickerNonce, setAccountDefaultPickerNonce] = useState(0);
  /** Account Modal 內分頁：identity = 帳號/密碼/角色，customers = 客戶/預設客戶 */
  const [accountSettingsTab, setAccountSettingsTab] = useState('identity');

  // ─── OTP Settings state（WhatsApp + 電郵）────────────────
  const [otpSubTab, setOtpSubTab] = useState('whatsapp');
  const [otpSettingsLoading, setOtpSettingsLoading] = useState(false);
  const [otpSettingsSaving, setOtpSettingsSaving] = useState(false);
  const [otpEmailSaving, setOtpEmailSaving] = useState(false);
  const [otpTemplates, setOtpTemplates] = useState([]);
  const [otpTemplatesLoading, setOtpTemplatesLoading] = useState(false);
  const [otpTemplateId, setOtpTemplateId] = useState('');
  const [otpBodyText, setOtpBodyText] = useState('');
  const [otpTemplateName, setOtpTemplateName] = useState('');
  const [otpTemplateCategory, setOtpTemplateCategory] = useState('AUTHENTICATION');
  const [otpTemplateLanguage, setOtpTemplateLanguage] = useState('zh_TW');
  const [otpEmailSubject, setOtpEmailSubject] = useState('');
  const [otpEmailBody, setOtpEmailBody] = useState('');
  const [otpTplCreating, setOtpTplCreating] = useState(false);

  // ─── Welcome Message state ───────────────────────────────
  const [welcomeSubTab, setWelcomeSubTab] = useState('whatsapp');
  const [welcomeSettingsLoading, setWelcomeSettingsLoading] = useState(false);
  const [welcomeSettingsSaving, setWelcomeSettingsSaving] = useState(false);
  const [welcomeEmailSaving, setWelcomeEmailSaving] = useState(false);
  const [welcomeTemplates, setWelcomeTemplates] = useState([]);
  const [welcomeTemplatesLoading, setWelcomeTemplatesLoading] = useState(false);
  const [welcomeTemplateId, setWelcomeTemplateId] = useState('');
  const [welcomeBodyText, setWelcomeBodyText] = useState('');
  const [welcomeTemplateName, setWelcomeTemplateName] = useState('');
  const [welcomeTemplateCategory, setWelcomeTemplateCategory] = useState('MARKETING');
  const [welcomeTemplateLanguage, setWelcomeTemplateLanguage] = useState('zh_TW');
  const [welcomeEmailSubject, setWelcomeEmailSubject] = useState('');
  const [welcomeEmailBody, setWelcomeEmailBody] = useState('');
  const [welcomeTplCreating, setWelcomeTplCreating] = useState(false);
  const [welcomeTplForm] = Form.useForm();

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  }, []);

  const companyQuery = useMemo(() => {
    if (!isTenantAdmin || !companyFilter) return '';
    return `?companyId=${companyFilter}`;
  }, [isTenantAdmin, companyFilter]);

  const companyParam = useMemo(() => {
    if (!isTenantAdmin || !companyFilter) return null;
    return companyFilter;
  }, [isTenantAdmin, companyFilter]);

  const loadCompanies = useCallback(async () => {
    if (!isTenantAdmin) return;
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompaniesForSelect(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
  }, [fetchWithAuth, isTenantAdmin]);

  const extractResponseError = async (response, logTag = 'WpaPermission') => {
    const text = await response.text();
    if (!text) {
      console.error(`[${logTag}] HTTP ${response.status} — empty response body`, {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      });
      return `HTTP ${response.status}`;
    }
    try {
      const data = JSON.parse(text);
      console.error(`[${logTag}] HTTP ${response.status}`, {
        status: response.status,
        url: response.url,
        body: data,
      });
      return data?.error || data?.detail || data?.message || text;
    } catch {
      console.error(`[${logTag}] HTTP ${response.status} — non-JSON body`, {
        status: response.status,
        url: response.url,
        body: text,
      });
      return text;
    }
  };

  const maskAccountPayloadForLog = (payload) => ({
    ...payload,
    password: payload.password ? '***' : undefined,
    newPassword: payload.newPassword ? '***' : undefined,
  });

  const label = (key, fallback) => {
    const val = t(key);
    return val === key ? fallback : val;
  };

  // ─── WhatsApp OTP Settings ───────────────────────────────

  const clearOtpSettings = useCallback(() => {
    setOtpTemplateId('');
    setOtpTemplateName('');
    setOtpBodyText('');
    setOtpTemplateCategory('AUTHENTICATION');
    setOtpTemplateLanguage('zh_TW');
    setOtpEmailSubject('');
    setOtpEmailBody('');
  }, []);

  const loadOtpSettings = useCallback(async () => {
    setOtpSettingsLoading(true);
    clearOtpSettings();
    try {
      const res = await fetchWithAuth(`/api/wpa-otp-settings${companyQuery}`);
      if (!res.ok) {
        // 404 = 該公司尚未設定，保持空白
        return;
      }
      const json = await res.json();
      setOtpTemplateId(json.templateId || json.templateName || '');
      setOtpTemplateName(json.templateName || '');
      setOtpBodyText(json.bodyText || '');
      setOtpTemplateCategory(json.templateCategory || 'AUTHENTICATION');
      setOtpTemplateLanguage(json.templateLanguage || 'zh_TW');
      setOtpEmailSubject(json.emailSubject || '');
      setOtpEmailBody(json.emailBody || '');
    } catch {
      // 網絡錯誤也清空，避免顯示其他公司的資料
    } finally {
      setOtpSettingsLoading(false);
    }
  }, [fetchWithAuth, companyQuery, clearOtpSettings]);

  const loadOtpTemplates = useCallback(async () => {
    setOtpTemplatesLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: '200' });
      if (companyParam) params.set('companyId', companyParam);
      const res = await fetchWithAuth(`/api/whatsappmetatemplates?${params}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      // 只顯示 body 含 {{1}} 的 templates，方便辨識 OTP 用途
      const all = json.data || json.templates || [];
      // 顯示 AUTHENTICATION 類別，或 body 含 {{1}} 的 UTILITY/MARKETING 模板
      const filtered = all.filter((tpl) => {
        const cat = (tpl.category || '').toUpperCase();
        if (cat === 'AUTHENTICATION') return true;
        const body = tpl.components?.find((c) => c.type === 'BODY')?.text || '';
        return body.includes('{{1}}');
      });
      setOtpTemplates(filtered.length > 0 ? filtered : all.slice(0, 50));
    } catch (err) {
      message.error(err.message);
    } finally {
      setOtpTemplatesLoading(false);
    }
  }, [fetchWithAuth, companyParam]);

  const saveOtpSettings = async (overrideName, overrideLanguage, overrideCategory, overrideBody) => {
    const name = overrideName ?? otpTemplateName;
    const language = overrideLanguage ?? otpTemplateLanguage;
    const category = overrideCategory ?? otpTemplateCategory;
    const body = overrideBody ?? otpBodyText;
    if (!name) { message.warning('請先從下方列表選擇一個已審批的 Template'); return; }
    if (category !== 'AUTHENTICATION' && !(body || '').includes('{{1}}')) {
      message.warning('UTILITY 類別的訊息內容必須包含 {{1}} 作為 OTP 碼的位置');
      return;
    }
    setOtpSettingsSaving(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-otp-settings${companyQuery}`, {
        method: 'PUT',
        body: JSON.stringify({
          templateName: name,
          templateLanguage: language,
          templateCategory: category,
          bodyText: category === 'AUTHENTICATION' ? null : body,
        }),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.otpWhatsappSaved', 'WhatsApp OTP 設定已儲存'));
    } catch (err) {
      message.error(err.message);
    } finally {
      setOtpSettingsSaving(false);
    }
  };

  const saveEmailOtpSettings = async () => {
    const subject = (otpEmailSubject || '').trim();
    const body = (otpEmailBody || '').trim();
    if (!subject || !body) {
      message.warning(label('wpaPermissions.otpEmailRequired', '請填寫電郵主旨與正文'));
      return;
    }
    if (!subject.includes('{{1}}') && !body.includes('{{1}}')) {
      message.warning(label('wpaPermissions.otpEmailPlaceholderRequired', '主旨或正文至少須包含 {{1}} 作為 OTP 驗證碼占位符'));
      return;
    }
    setOtpEmailSaving(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-otp-settings/email${companyQuery}`, {
        method: 'PUT',
        body: JSON.stringify({ emailSubject: subject, emailBody: body }),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.otpEmailSaved', '電郵 OTP 模板已儲存'));
    } catch (err) {
      message.error(err.message);
    } finally {
      setOtpEmailSaving(false);
    }
  };

  const clearWelcomeSettings = useCallback(() => {
    setWelcomeTemplateId('');
    setWelcomeTemplateName('');
    setWelcomeBodyText('');
    setWelcomeTemplateCategory('MARKETING');
    setWelcomeTemplateLanguage('zh_TW');
    setWelcomeEmailSubject('');
    setWelcomeEmailBody('');
  }, []);

  const loadWelcomeSettings = useCallback(async () => {
    setWelcomeSettingsLoading(true);
    clearWelcomeSettings();
    try {
      const res = await fetchWithAuth(`/api/wpa-welcome-settings${companyQuery}`);
      if (!res.ok) {
        // 404 = 該公司尚未設定，保持空白
        return;
      }
      const json = await res.json();
      setWelcomeTemplateId(json.templateName || '');
      setWelcomeTemplateName(json.templateName || '');
      setWelcomeBodyText(json.bodyText || '');
      setWelcomeTemplateCategory(json.templateCategory || 'MARKETING');
      setWelcomeTemplateLanguage(json.templateLanguage || 'zh_TW');
      setWelcomeEmailSubject(json.emailSubject || '');
      setWelcomeEmailBody(json.emailBody || '');
    } catch {
      // 網絡錯誤也清空，避免顯示其他公司的資料
    } finally {
      setWelcomeSettingsLoading(false);
    }
  }, [fetchWithAuth, companyQuery, clearWelcomeSettings]);

  const loadWelcomeTemplates = useCallback(async () => {
    setWelcomeTemplatesLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: '200' });
      if (companyParam) params.set('companyId', companyParam);
      const res = await fetchWithAuth(`/api/whatsappmetatemplates?${params}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      const all = json.data || json.templates || [];
      setWelcomeTemplates(all.slice(0, 100));
    } catch (err) {
      message.error(err.message);
    } finally {
      setWelcomeTemplatesLoading(false);
    }
  }, [fetchWithAuth, companyParam]);

  const saveWelcomeWhatsappSettings = async (overrideName, overrideLanguage, overrideCategory, overrideBody) => {
    const name = overrideName ?? welcomeTemplateName;
    const language = overrideLanguage ?? welcomeTemplateLanguage;
    const category = overrideCategory ?? welcomeTemplateCategory;
    const body = overrideBody ?? welcomeBodyText;
    if (!name) {
      message.warning(label('wpaPermissions.welcomeSelectTemplate', '請先選擇已審批的 Template'));
      return;
    }
    setWelcomeSettingsSaving(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-welcome-settings${companyQuery}`, {
        method: 'PUT',
        body: JSON.stringify({
          templateName: name,
          templateLanguage: language,
          templateCategory: category,
          bodyText: body || null,
        }),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.welcomeWhatsappSaved', 'WhatsApp 歡迎訊息設定已儲存'));
    } catch (err) {
      message.error(err.message);
    } finally {
      setWelcomeSettingsSaving(false);
    }
  };

  const saveWelcomeEmailSettings = async () => {
    const subject = (welcomeEmailSubject || '').trim();
    const body = (welcomeEmailBody || '').trim();
    if (!subject || !body) {
      message.warning(label('wpaPermissions.welcomeEmailRequired', '請填寫電郵主旨與正文'));
      return;
    }
    setWelcomeEmailSaving(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-welcome-settings/email${companyQuery}`, {
        method: 'PUT',
        body: JSON.stringify({ emailSubject: subject, emailBody: body }),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.welcomeEmailSaved', '電郵歡迎訊息模板已儲存'));
    } catch (err) {
      message.error(err.message);
    } finally {
      setWelcomeEmailSaving(false);
    }
  };

  const createWelcomeTemplate = async (values) => {
    const category = (values.category || 'MARKETING').toUpperCase();
    const bodyText = (values.bodyText || '').trim();
    if (category !== 'AUTHENTICATION' && !bodyText) {
      message.warning(label('wpaPermissions.welcomeBodyRequired', '請輸入模板正文'));
      return;
    }
    setWelcomeTplCreating(true);
    try {
      const components = [];
      if (category === 'AUTHENTICATION') {
        components.push({ type: 'BODY', add_security_recommendation: true });
        if (values.footer) components.push({ type: 'FOOTER', code_expiration_minutes: 10 });
      } else {
        components.push({ type: 'BODY', text: bodyText });
        if (values.footer?.trim()) {
          components.push({ type: 'FOOTER', text: values.footer.trim() });
        }
      }
      const token = localStorage.getItem('token');
      const payload = { name: values.name, category, language: values.language, components };
      const qs = companyParam ? `?companyId=${companyParam}` : '';
      const res = await fetch(`/api/whatsappmetatemplates${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        const metaMsg = data.message ? (() => {
          try {
            const m = JSON.parse(data.message.slice(data.message.indexOf('{')));
            return m?.error?.error_user_msg || m?.error?.message || null;
          } catch { return null; }
        })() : null;
        throw new Error(metaMsg || data.error || data.message || `HTTP ${res.status}`);
      }
      message.success(label('wpaPermissions.welcomeTplSubmitted', 'Template 已提交 Meta 審核'));
      welcomeTplForm.resetFields();
      loadWelcomeTemplates();
    } catch (err) {
      message.error(err.message);
    } finally {
      setWelcomeTplCreating(false);
    }
  };

  const createOtpTemplate = async (values) => {
    setOtpTplCreating(true);
    try {
      // 固定使用 AUTHENTICATION：Meta 自動產生文案
      const components = [
        { type: 'BODY', add_security_recommendation: true },
        { type: 'FOOTER', code_expiration_minutes: 10 },
        {
          type: 'BUTTONS',
          buttons: [{ type: 'OTP', otp_type: 'COPY_CODE' }],
        },
      ];
      const token = localStorage.getItem('token');
      const payload = { name: values.name, category: 'AUTHENTICATION', language: values.language, components };
      console.log('[OTP Template] 送出 payload:', JSON.stringify(payload, null, 2));
      const qs = companyParam ? `?companyId=${companyParam}` : '';
      const res = await fetch(`/api/whatsappmetatemplates${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      console.log('[OTP Template] 後端回應:', res.status, data);
      if (!res.ok || !data.success) {
        const metaMsg = data.message ? (() => {
          try {
            const m = JSON.parse(data.message.slice(data.message.indexOf('{')));
            return m?.error?.error_user_msg || m?.error?.message || null;
          } catch { return null; }
        })() : null;
        throw new Error(metaMsg || data.error || data.message || `HTTP ${res.status}`);
      }
      message.success('Template 已提交 Meta 審核！審核通過後可在下方列表設為啟用。');
      otpTplForm.resetFields();
      loadOtpTemplates();
    } catch (err) {
      message.error(err.message);
    } finally {
      setOtpTplCreating(false);
    }
  };

  // ─── Roles ──────────────────────────────────────────────

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/roles${companyQuery}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      setRoles(json.data || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setRolesLoading(false);
    }
  }, [fetchWithAuth, companyQuery]);

  const handleSaveRole = async () => {
    try {
      const values = await roleForm.validateFields();
      setRoleSaving(true);
      const res = await fetchWithAuth(`/api/wpa-permissions/roles/${editingRole.id}`, {
        method: 'PUT',
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.roleSaved', 'Role saved'));
      setRoleModalOpen(false);
      loadRoles();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    } finally {
      setRoleSaving(false);
    }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    roleForm.setFieldsValue({
      displayName: role.displayName,
      description: role.description,
    });
    setRoleModalOpen(true);
  };

  // ─── Groups ─────────────────────────────────────────────

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups${companyQuery}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      setGroups(json.data || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setGroupsLoading(false);
    }
  }, [fetchWithAuth, companyQuery]);

  const handleSaveGroup = async () => {
    try {
      const values = await groupForm.validateFields();
      setGroupSaving(true);
      const isEdit = !!editingGroup?.id;
      const url = isEdit
        ? `/api/wpa-permissions/groups/${editingGroup.id}`
        : '/api/wpa-permissions/groups';
      const payload = {
        ...values,
        defaultCustomerId: groupEditDefaultCustomer?.customerId || groupEditDefaultCustomer?.id || null,
      };
      const res = await fetchWithAuth(url, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.groupSaved', 'Group saved'));
      setGroupModalOpen(false);
      loadGroups();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    } finally {
      setGroupSaving(false);
    }
  };

  const handleDeleteGroup = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.groupDeleted', 'Group deleted'));
      if (expandedGroupId === id) setExpandedGroupId(null);
      loadGroups();
    } catch (err) {
      message.error(err.message);
    }
  };

  const openCreateGroup = () => {
    setEditingGroup(null);
    groupForm.resetFields();
    setGroupEditCustomers([]);
    setGroupEditDefaultCustomer(null);
    setGroupModalOpen(true);
  };

  const openEditGroup = (record) => {
    setEditingGroup(record);
    groupForm.setFieldsValue({
      groupName: record.groupName,
      description: record.description,
      roleId: record.roleId,
    });
    setGroupEditCustomers([]);
    setGroupEditDefaultCustomer(null);
    loadGroupCustomersForEdit(record.id, record.defaultCustomerId);
    setGroupModalOpen(true);
  };

  const loadGroupCustomersForEdit = async (groupId, defaultCustomerId) => {
    setGroupEditCustomersLoading(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${groupId}/customers`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      const customers = json.data || [];
      setGroupEditCustomers(customers);
      const defaultCustomer = customers.find((c) => c.customerId === defaultCustomerId || c.id === defaultCustomerId) || null;
      setGroupEditDefaultCustomer(defaultCustomer);
      return customers;
    } catch (err) {
      message.error(err.message);
      setGroupEditCustomers([]);
      setGroupEditDefaultCustomer(null);
      return [];
    } finally {
      setGroupEditCustomersLoading(false);
    }
  };

  const mapRowToGroupEditDefaultShape = useCallback((row) => {
    const nl = parseCustomerNameLocalesRoot(row.name_locales);
    const display = resolveCustomerDisplayName(row.name_1, row.name_2, row.name_3, nl, currentLanguage);
    return {
      customerId: row.id,
      customerNo: row.customer_no,
      branchNo: row.branch_no,
      nameCn: display,
      nameEn: row.name_1 || '',
    };
  }, [currentLanguage]);

  const syncGroupEditCustomersFromPicker = async ({ customerIds }) => {
    if (!editingGroup?.id) return;
    const cur = groupEditCustomers;
    const target = new Set(customerIds.map(String));
    const toRemove = cur.filter((c) => !target.has(String(c.customerId)));
    const curIds = new Set(cur.map((c) => String(c.customerId)));
    const toAdd = customerIds.filter((id) => !curIds.has(String(id)));
    try {
      for (const entry of toRemove) {
        const res = await fetchWithAuth(`/api/wpa-permissions/groups/${editingGroup.id}/customers/${entry.id}`, {
          method: 'DELETE',
        });
        if (!res.ok) throw new Error(await extractResponseError(res));
      }
      if (toAdd.length) {
        const res = await fetchWithAuth(`/api/wpa-permissions/groups/${editingGroup.id}/customers`, {
          method: 'POST',
          body: JSON.stringify({ customerIds: toAdd }),
        });
        if (!res.ok) throw new Error(await extractResponseError(res));
      }
      const defaultId = groupEditDefaultCustomer?.customerId || groupEditDefaultCustomer?.id;
      const defaultRemoved = toRemove.some((e) => e.customerId === defaultId);
      message.success(label('wpaPermissions.groupCustomersUpdated', 'Customers updated'));
      await loadGroupCustomersForEdit(editingGroup.id, defaultRemoved ? null : defaultId);
      if (defaultRemoved) setGroupEditDefaultCustomer(null);
      loadGroups();
      setGroupEditCustomerPickerOpen(false);
    } catch (err) {
      message.error(err.message);
    }
  };

  const applyGroupEditDefaultFromPicker = async ({ rows }) => {
    if (!rows?.[0]) {
      setGroupEditCustomerPickerOpen(false);
      return;
    }
    const id = rows[0].id;
    if (!editingGroup?.id) {
      setGroupEditDefaultCustomer(mapRowToGroupEditDefaultShape(rows[0]));
      setGroupEditCustomerPickerOpen(false);
      return;
    }
    try {
      const inList = groupEditCustomers.some((c) => String(c.customerId) === String(id));
      if (inList) {
        const entry = groupEditCustomers.find((c) => String(c.customerId) === String(id));
        if (entry) setGroupEditDefaultCustomer(entry);
        setGroupEditCustomerPickerOpen(false);
        return;
      }
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${editingGroup.id}/customers`, {
        method: 'POST',
        body: JSON.stringify({ customerIds: [id] }),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      await loadGroupCustomersForEdit(editingGroup.id, id);
      loadGroups();
      setGroupEditCustomerPickerOpen(false);
    } catch (err) {
      message.error(err.message);
    }
  };

  const openGroupEditCustomerTablePicker = () => {
    setGroupEditPickerInitialKeys(groupEditCustomers.map((c) => c.customerId).filter(Boolean));
    setGroupEditCustomerPickerMode('customers');
    setGroupEditCustomerPickerNonce((n) => n + 1);
    setGroupEditCustomerPickerOpen(true);
  };

  const openGroupEditDefaultTablePicker = () => {
    const cid = groupEditDefaultCustomer?.customerId || groupEditDefaultCustomer?.id;
    setGroupEditPickerInitialKeys(cid ? [cid] : []);
    setGroupEditCustomerPickerMode('default');
    setGroupEditCustomerPickerNonce((n) => n + 1);
    setGroupEditCustomerPickerOpen(true);
  };

  const removeGroupEditCustomer = async (entryId) => {
    if (!editingGroup?.id) return;
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${editingGroup.id}/customers/${entryId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.customerRemoved', 'Customer removed'));
      const removed = groupEditCustomers.find((c) => c.id === entryId);
      const currentDefaultId = groupEditDefaultCustomer?.customerId || groupEditDefaultCustomer?.id;
      if (removed && currentDefaultId === removed.customerId) {
        setGroupEditDefaultCustomer(null);
      }
      loadGroupCustomersForEdit(
        editingGroup.id,
        removed && currentDefaultId === removed.customerId ? null : currentDefaultId
      );
      loadGroups();
    } catch (err) {
      message.error(err.message);
    }
  };

  // Group members & customers
  const loadGroupMembers = useCallback(async (groupId) => {
    setGroupMembersLoading(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${groupId}/members`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      setGroupMembers(json.data || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setGroupMembersLoading(false);
    }
  }, [fetchWithAuth]);

  const loadGroupCustomers = useCallback(async (groupId) => {
    setGroupCustomersLoading(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${groupId}/customers`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      setGroupCustomers(json.data || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setGroupCustomersLoading(false);
    }
  }, [fetchWithAuth]);

  const expandGroup = (groupId) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(groupId);
    setGroupDetailTab('members');
    loadGroupMembers(groupId);
    loadGroupCustomers(groupId);
  };

  const searchMembers = async (text) => {
    if (!text || text.length < 2) { setMemberSearchResults([]); return; }
    setMemberSearchLoading(true);
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/accounts/search?q=${encodeURIComponent(text)}&limit=20`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      setMemberSearchResults(json.data || []);
    } catch (err) {
      message.error(err.message);
    } finally {
      setMemberSearchLoading(false);
    }
  };

  const addMembers = async (identityIds) => {
    if (!identityIds || identityIds.length === 0) return;
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${expandedGroupId}/members`, {
        method: 'POST',
        body: JSON.stringify({ identityIds }),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.memberAdded', 'Member added'));
      loadGroupMembers(expandedGroupId);
      loadGroups();
    } catch (err) {
      message.error(err.message);
    }
  };

  const addMember = async (identityId) => addMembers([identityId]);

  const removeMember = async (memberId) => {
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${expandedGroupId}/members/${memberId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.memberRemoved', 'Member removed'));
      loadGroupMembers(expandedGroupId);
      loadGroups();
    } catch (err) {
      message.error(err.message);
    }
  };

  const removeSelectedMembers = async () => {
    if (!expandedGroupId || groupMemberSelectedRowKeys.length === 0) return;
    try {
      await Promise.all(
        groupMemberSelectedRowKeys.map((memberId) =>
          fetchWithAuth(`/api/wpa-permissions/groups/${expandedGroupId}/members/${memberId}`, {
            method: 'DELETE',
          }).then(async (res) => {
            if (!res.ok) throw new Error(await extractResponseError(res));
          })
        )
      );
      message.success(label('wpaPermissions.memberRemoved', 'Member removed'));
      setGroupMemberSelectedRowKeys([]);
      loadGroupMembers(expandedGroupId);
      loadGroups();
    } catch (err) {
      message.error(err.message);
    }
  };

  const applyExpandedGroupCustomersPicker = async ({ customerIds }) => {
    if (!expandedGroupId) return;
    const existing = new Set(groupCustomers.map((c) => String(c.customerId)));
    const toAdd = customerIds.filter((id) => !existing.has(String(id)));
    if (!toAdd.length) {
      setAddCustomerModalOpen(false);
      return;
    }
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${expandedGroupId}/customers`, {
        method: 'POST',
        body: JSON.stringify({ customerIds: toAdd }),
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.customerAdded', 'Customer added'));
      loadGroupCustomers(expandedGroupId);
      loadGroups();
      setAddCustomerModalOpen(false);
    } catch (err) {
      message.error(err.message);
    }
  };

  const removeCustomer = async (entryId) => {
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/groups/${expandedGroupId}/customers/${entryId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.customerRemoved', 'Customer removed'));
      loadGroupCustomers(expandedGroupId);
      loadGroups();
    } catch (err) {
      message.error(err.message);
    }
  };

  // ─── Accounts (PWA Account 直接 CRUD) ─────────────────────

  const loadAccounts = useCallback(async (page, pageSize, search) => {
    setAccountsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('page', page);
      params.set('pageSize', pageSize);
      if (companyParam) params.set('companyId', companyParam);
      const res = await fetchWithAuth(`/api/wpa-permissions/accounts?${params}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      setAccounts(json.data || []);
      setAccountTotal(json.total || 0);
    } catch (err) {
      message.error(err.message);
    } finally {
      setAccountsLoading(false);
    }
  }, [fetchWithAuth, companyParam]);

  const openCreateAccount = async () => {
    setEditingAccount(null);
    setAccountSettingsTab('identity');
    accountForm.resetFields();
    const defaultUserRoleId =
      roles.find((r) => r.roleKey === 'wpa_user')?.id
      ?? roles.find((r) => String(r.displayName || '').toLowerCase() === 'user')?.id;
    accountForm.setFieldsValue({
      loginId: '',
      displayName: '',
      phoneNo: '',
      email: '',
      password: '',
      newPassword: '',
      status: 'active',
      roleId: defaultUserRoleId,
      customerIds: [],
      defaultCustomerId: undefined,
    });
    setAccountCustomerLabelRows([]);
    setAccountModalOpen(true);
    try {
      const password = await generatePasswordForScope(PASSWORD_POLICY_SCOPES.wpaAccount);
      if (password) {
        accountForm.setFieldsValue({ password });
      }
    } catch (err) {
      console.error('[openCreateAccount] auto-generate password failed', err);
    }
  };

  const openEditAccount = async (account) => {
    setAccountSettingsTab('identity');
    setAccountModalOpen(true);
    setAccountLoading(true);
    setEditingAccount({ id: account.id });
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/accounts/${account.id}${companyQuery}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const json = await res.json();
      const data = json.data || null;
      setEditingAccount(data);

      const customerIds = data?.customers?.map((c) => c.customerId) || [];
        accountForm.setFieldsValue({
        loginId: data?.loginId || '',
        displayName: data?.displayName || '',
        phoneNo: data?.phoneNo || '',
        email: data?.email || '',
        password: '',
        newPassword: '',
        status: data?.status || 'active',
        roleId: data?.roleId || undefined,
        customerIds,
        defaultCustomerId: data?.defaultCustomerId || undefined,
      });
      if (data?.customers?.length) {
        setAccountCustomerLabelRows(
          data.customers.map((c) => ({
            id: c.customerId,
            customer_no: c.customerNo,
            branch_no: c.branchNo || '',
            name_1: '',
            name_2: '',
            name_3: '',
            name_locales: null,
            labelText: c.displayName || c.nameCn || c.nameEn || c.customerNo || '—',
          }))
        );
      } else {
        setAccountCustomerLabelRows([]);
      }
    } catch (err) {
      message.error(err.message);
      setEditingAccount(null);
      setAccountCustomerLabelRows([]);
    } finally {
      setAccountLoading(false);
    }
  };

  const handleSaveAccount = async (options = {}) => {
    const { resendWelcomeWithNewPassword = false, closeModal = true } = options;
    try {
      await accountForm.validateFields();
      const values = accountForm.getFieldsValue(true);
      const loginId = (values.loginId || '').trim();
      if (!loginId) {
        message.error(label('wpaPermissions.loginIdRequired', '請填寫登入帳號'));
        return;
      }
      const isCreate = !editingAccount?.id;
      if (isCreate && !values.password) {
        message.error(label('wpaPermissions.passwordRequiredForCreate', '建立帳號須設定密碼'));
        return;
      }

      const payload = {
        loginId,
        displayName: values.displayName || null,
        phoneNo: values.phoneNo || null,
        email: values.email || null,
        status: values.status || 'active',
        roleId: values.roleId || null,
        customerIds: values.customerIds || [],
        defaultCustomerId: values.defaultCustomerId || null,
        ...(companyParam ? { companyId: companyParam } : {}),
      };
      if (isCreate) {
        payload.password = values.password;
      } else if (resendWelcomeWithNewPassword) {
        payload.resendWelcomeWithNewPassword = true;
      } else if (values.newPassword) {
        payload.newPassword = values.newPassword;
      }

      setAccountSaving(true);
      const url = isCreate
        ? '/api/wpa-permissions/accounts'
        : `/api/wpa-permissions/accounts/${editingAccount.id}`;
      const method = isCreate ? 'POST' : 'PUT';
      console.log('[handleSaveAccount] request', {
        method,
        url,
        isCreate,
        payload: maskAccountPayloadForLog(payload),
      });
      const res = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractResponseError(res, 'handleSaveAccount'));
      const json = await res.json().catch(() => ({}));
      console.log('[handleSaveAccount] success', { method, url, response: json });
      const generatedPassword = json?.data?.generatedPassword;
      if (generatedPassword) {
        accountForm.setFieldsValue({ newPassword: generatedPassword });
        message.success(
          label(
            'wpaPermissions.saveAndResendPasswordSuccess',
            '已儲存、重設密碼並重發 welcome 訊息'
          )
        );
      } else {
        message.success(label('wpaPermissions.accountSaved', 'Account saved'));
      }
      if (closeModal) setAccountModalOpen(false);
      loadAccounts(accountPage, accountPageSize, accountSearch);
    } catch (err) {
      if (err.errorFields) return;
      console.error('[handleSaveAccount] failed', err);
      message.error(err.message);
    } finally {
      setAccountSaving(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/wpa-permissions/accounts/${id}${companyQuery}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await extractResponseError(res));
      message.success(label('wpaPermissions.accountDeleted', 'Account deleted'));
      if (editingAccount?.id === id) setAccountModalOpen(false);
      loadAccounts(accountPage, accountPageSize, accountSearch);
    } catch (err) {
      message.error(err.message);
    }
  };

  const openAccountCustomerPicker = () => {
    setAccountPickerInitialKeys(accountForm.getFieldValue('customerIds') || []);
    setAccountCustomerPickerNonce((n) => n + 1);
    setAccountCustomerPickerOpen(true);
  };

  const handleAccountCustomerPickerApply = ({ customerIds: keys, rows: pickedRows }) => {
    const keySet = new Set(keys.map((k) => String(k)));
    const mapped = keys.map((kid) => {
      const row = pickedRows.find((r) => String(r.id) === String(kid));
      if (row) return { ...row };
      const fallback = accountCustomerLabelRows.find((r) => String(r.id) === String(kid));
      return fallback || { id: kid, customer_no: String(kid), labelText: String(kid) };
    });
    const currentDefault = accountForm.getFieldValue('defaultCustomerId');
    const nextDefault =
      currentDefault && keySet.has(String(currentDefault)) ? currentDefault : undefined;
    accountForm.setFieldsValue({ customerIds: keys, defaultCustomerId: nextDefault });
    setAccountCustomerLabelRows(mapped);
    setAccountCustomerPickerOpen(false);
  };

  const removeAccountCustomerRow = (customerId) => {
    const ids = (accountForm.getFieldValue('customerIds') || []).filter(
      (x) => String(x) !== String(customerId)
    );
    const nextLabelRows = accountCustomerLabelRows.filter((r) => String(r.id) !== String(customerId));
    const currentDefault = accountForm.getFieldValue('defaultCustomerId');
    const nextDefault =
      String(currentDefault) === String(customerId) ? undefined : currentDefault;
    accountForm.setFieldsValue({ customerIds: ids, defaultCustomerId: nextDefault });
    setAccountCustomerLabelRows(nextLabelRows);
  };

  const openAccountDefaultTablePicker = () => {
    const did = accountForm.getFieldValue('defaultCustomerId');
    setAccountDefaultPickerInitialKeys(did ? [did] : []);
    setAccountDefaultPickerNonce((n) => n + 1);
    setAccountDefaultCustomerPickerOpen(true);
  };

  const handleAccountDefaultPickerApply = ({ rows }) => {
    const row = rows?.[0];
    if (!row) {
      setAccountDefaultCustomerPickerOpen(false);
      return;
    }
    const id = row.id;
    let customerIds = accountForm.getFieldValue('customerIds') || [];
    let labelRows = accountCustomerLabelRows;
    if (!customerIds.some((x) => String(x) === String(id))) {
      customerIds = [...customerIds, id];
      labelRows = [...labelRows, { ...row }];
    }
    accountForm.setFieldsValue({ customerIds, defaultCustomerId: id });
    setAccountCustomerLabelRows(labelRows);
    setAccountDefaultCustomerPickerOpen(false);
  };

  const accountCustomerRowDisplayName = useCallback(
    (r) => {
      if (r?.labelText) return r.labelText;
      const nl = parseCustomerNameLocalesRoot(r.name_locales);
      return (
        resolveCustomerDisplayName(r.name_1, r.name_2, r.name_3, nl, currentLanguage) ||
        r.customer_no ||
        '—'
      );
    },
    [currentLanguage]
  );

  const accountCustomersTableRows = useMemo(() => {
    const ids = watchedAccountCustomerIds || [];
    return ids.map((id) => {
      const r = accountCustomerLabelRows.find((x) => String(x.id) === String(id));
      const name = r ? accountCustomerRowDisplayName(r) : '—';
      return {
        rowKey: id,
        customerId: id,
        customerNo: r?.customer_no || '',
        branchNo: r?.branch_no != null && r.branch_no !== '' ? r.branch_no : '0000',
        nameCn: name,
        nameEn: r?.name_1 || '',
      };
    }).sort((a, b) => {
      const cmp = a.customerNo.localeCompare(b.customerNo);
      if (cmp !== 0) return cmp;
      return a.branchNo.localeCompare(b.branchNo);
    });
  }, [watchedAccountCustomerIds, accountCustomerLabelRows, accountCustomerRowDisplayName]);

  const accountDefaultTableRow = useMemo(() => {
    if (!watchedAccountDefaultCustomerId) return null;
    const r = accountCustomerLabelRows.find(
      (x) => String(x.id) === String(watchedAccountDefaultCustomerId)
    );
    const name = r ? accountCustomerRowDisplayName(r) : '—';
    return {
      customerId: watchedAccountDefaultCustomerId,
      customerNo: r?.customer_no || '',
      branchNo: r?.branch_no != null && r.branch_no !== '' ? r.branch_no : '0000',
      nameCn: name,
      nameEn: r?.name_1 || '',
    };
  }, [
    watchedAccountDefaultCustomerId,
    accountCustomerLabelRows,
    accountCustomerRowDisplayName
  ]);

  // ─── Load company list (Tenant_Admin only) ──────────────

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (isTenantAdmin && !companyFilter && companiesForSelect.length === 1) {
      setCompanyFilter(companiesForSelect[0].id);
    }
  }, [isTenantAdmin, companyFilter, companiesForSelect]);

  // ─── Tab switching triggers data load ───────────────────

  useEffect(() => {
    if (activeTab === 'roles') loadRoles();
    else if (activeTab === 'groups') { loadRoles(); loadGroups(); }
    else if (activeTab === 'accounts') { loadRoles(); loadAccounts(1, accountPageSize, ''); }
    else if (activeTab === 'otp-settings') { loadOtpSettings(); loadOtpTemplates(); }
    else if (activeTab === 'welcome-settings') { loadWelcomeSettings(); loadWelcomeTemplates(); }
  }, [activeTab, companyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render: Roles Tab ──────────────────────────────────

  const renderRolesTab = () => (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>
          {label('wpaPermissions.rolesTitle', 'PWA Roles')}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={loadRoles} loading={rolesLoading}>
          {label('common.refresh', 'Refresh')}
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {roles.map((role) => (
          <Card
            key={role.id}
            title={
              <Space>
                <UserOutlined />
                <span>{role.displayName || role.roleKey}</span>
              </Space>
            }
            extra={
              <Button type="link" icon={<EditOutlined />} onClick={() => openEditRole(role)}>
                {label('common.edit', 'Edit')}
              </Button>
            }
          >
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary">Role Key: </Text>
              <Tag>{role.roleKey}</Tag>
            </div>
            {role.description && (
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">{label('wpaPermissions.description', 'Description')}: </Text>
                <Text>{role.description}</Text>
              </div>
            )}
            <Space size="large">
              <div>
                <Text type="secondary">{label('wpaPermissions.groups', 'Groups')}: </Text>
                <Text strong>{role.groupCount ?? 0}</Text>
              </div>
              <div>
                <Text type="secondary">{label('wpaPermissions.accounts', 'Accounts')}: </Text>
                <Text strong>{role.contactCount ?? 0}</Text>
              </div>
            </Space>
          </Card>
        ))}
      </div>

      <Modal
        title={label('wpaPermissions.editRole', 'Edit Role')}
        open={roleModalOpen}
        onCancel={() => setRoleModalOpen(false)}
        onOk={handleSaveRole}
        confirmLoading={roleSaving}
        destroyOnClose
      >
        <Form form={roleForm} layout="vertical">
          <Form.Item
            name="displayName"
            label={label('wpaPermissions.displayName', 'Display Name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label={label('wpaPermissions.description', 'Description')}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  // ─── Render: Groups Tab ─────────────────────────────────

  const groupColumns = [
    {
      title: label('wpaPermissions.groupName', 'Group Name'),
      dataIndex: 'groupName',
      key: 'groupName',
      render: (text, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => expandGroup(record.id)}>
          {text}
        </Button>
      ),
    },
    {
      title: label('wpaPermissions.role', 'Role'),
      dataIndex: 'roleDisplayName',
      key: 'roleDisplayName',
      render: (v) => v ? <Tag color="blue">{v}</Tag> : '-',
    },
    {
      title: label('wpaPermissions.members', 'Members'),
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 100,
      align: 'center',
    },
    {
      title: label('wpaPermissions.customers', 'Customers'),
      dataIndex: 'customerCount',
      key: 'customerCount',
      width: 100,
      align: 'center',
    },
    {
      title: label('wpaPermissions.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v) => (
        <Tag color={v === 'active' ? 'green' : 'default'}>
          {v || 'active'}
        </Tag>
      ),
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: label('common.actions', 'Actions'),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditGroup(record)} />
          <Popconfirm
            title={label('wpaPermissions.deleteGroupConfirm', 'Delete this group?')}
            onConfirm={() => handleDeleteGroup(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const memberColumns = [
    { title: label('wpaPermissions.loginId', 'Login ID'), dataIndex: 'loginId', key: 'loginId' },
    { title: label('wpaPermissions.name', 'Name'), dataIndex: 'contactName', key: 'contactName' },
    { title: label('wpaPermissions.phone', 'Phone'), dataIndex: 'phone', key: 'phone' },
    {
      title: label('common.actions', 'Actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title={label('wpaPermissions.removeMemberConfirm', 'Remove this member?')}
          onConfirm={() => removeMember(record.id)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const groupCustomerColumns = [
    { title: label('wpaPermissions.customerNo', 'Customer No'), dataIndex: 'customerNo', key: 'customerNo' },
    { title: label('wpaPermissions.branchNo', 'Branch No'), dataIndex: 'branchNo', key: 'branchNo' },
    { title: label('wpaPermissions.customerName', 'Name'), key: 'customerName', render: (_, r) => r.nameCn || r.nameEn || '-' },
    {
      title: label('common.actions', 'Actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title={label('wpaPermissions.removeCustomerConfirm', 'Remove this customer?')}
          onConfirm={() => removeCustomer(record.id)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const renderGroupDetail = () => {
    if (!expandedGroupId) return null;
    const group = groups.find((g) => g.id === expandedGroupId);
    if (!group) return null;

    return (
      <Card
        title={`${group.groupName} — ${label('wpaPermissions.details', 'Details')}`}
        style={{ marginTop: 16 }}
        extra={
          <Button size="small" onClick={() => setExpandedGroupId(null)}>
            {label('common.close', 'Close')}
          </Button>
        }
      >
        <Tabs
          activeKey={groupDetailTab}
          onChange={setGroupDetailTab}
          items={[
            {
              key: 'members',
              label: label('wpaPermissions.members', 'Members'),
              children: (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <Space>
                      <Button
                        type="primary"
                        icon={<UserAddOutlined />}
                        onClick={() => {
                          setAddMemberModalOpen(true);
                          setMemberSearchText('');
                          setMemberSearchResults([]);
                          setMemberSearchSelectedIds([]);
                        }}
                      >
                        {label('wpaPermissions.addMember', 'Add Member')}
                      </Button>
                      <Popconfirm
                        title={label('wpaPermissions.removeMemberConfirm', 'Remove this member?')}
                        onConfirm={removeSelectedMembers}
                        disabled={groupMemberSelectedRowKeys.length === 0}
                      >
                        <Button
                          danger
                          disabled={groupMemberSelectedRowKeys.length === 0}
                          icon={<DeleteOutlined />}
                        >
                          {label('common.remove', 'Remove')} ({groupMemberSelectedRowKeys.length})
                        </Button>
                      </Popconfirm>
                    </Space>
                  </div>
                  <Table
                    dataSource={groupMembers}
                    columns={memberColumns}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys: groupMemberSelectedRowKeys,
                      onChange: (keys) => setGroupMemberSelectedRowKeys(keys),
                    }}
                    loading={groupMembersLoading}
                    size="small"
                    pagination={false}
                  />
                </div>
              ),
            },
            {
              key: 'customers',
              label: label('wpaPermissions.customers', 'Customers'),
              children: (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setExpandedGroupCustomerPickerInitialKeys(
                          groupCustomers.map((c) => c.customerId).filter(Boolean)
                        );
                        setAddCustomerPickerNonce((n) => n + 1);
                        setAddCustomerModalOpen(true);
                      }}
                    >
                      {label('wpaPermissions.addCustomer', 'Add Customer')}
                    </Button>
                  </div>
                  <Table
                    dataSource={groupCustomers}
                    columns={groupCustomerColumns}
                    rowKey="id"
                    loading={groupCustomersLoading}
                    size="small"
                    pagination={false}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    );
  };

  const renderGroupsTab = () => (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>
          {label('wpaPermissions.groupsTitle', 'Permission Groups')}
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadGroups(); loadRoles(); }} loading={groupsLoading}>
            {label('common.refresh', 'Refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateGroup}>
            {label('wpaPermissions.createGroup', 'Create Group')}
          </Button>
        </Space>
      </div>

      <Table
        dataSource={groups}
        columns={groupColumns}
        rowKey="id"
        loading={groupsLoading}
        pagination={false}
        size="middle"
      />

      {renderGroupDetail()}

      {/* Group create/edit modal */}
      <Modal
        title={editingGroup?.id
          ? label('wpaPermissions.editGroup', 'Edit Group')
          : label('wpaPermissions.createGroup', 'Create Group')}
        open={groupModalOpen}
        onCancel={() => {
          setGroupModalOpen(false);
          setGroupEditCustomerPickerOpen(false);
        }}
        onOk={handleSaveGroup}
        confirmLoading={groupSaving}
        destroyOnClose
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item
            name="groupName"
            label={label('wpaPermissions.groupName', 'Group Name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label={label('wpaPermissions.description', 'Description')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="roleId"
            label={label('wpaPermissions.role', 'Role')}
            rules={[{ required: true }]}
          >
            <Select placeholder={label('wpaPermissions.selectRole', 'Select a role')}>
              {roles.map((r) => (
                <Select.Option key={r.id} value={r.id}>
                  {r.displayName || r.roleKey}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {editingGroup?.id && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>{label('wpaPermissions.customers', 'Customers')}</Text>
                <Button
                  type="default"
                  block
                  style={{ marginTop: 8 }}
                  onClick={openGroupEditCustomerTablePicker}
                >
                  {`${label('wpaPermissions.selectCustomersTable', '以表格選擇客戶')} (${groupEditCustomers.length})`}
                </Button>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  {label(
                    'wpaPermissions.selectCustomersTableHint',
                    '開啟與客戶主檔相同的列表，可搜尋、篩選公司（租戶管理員）、多選'
                  )}
                </Text>
              </div>
              <Table
                size="small"
                rowKey="id"
                loading={groupEditCustomersLoading}
                dataSource={groupEditCustomers}
                pagination={false}
                locale={{ emptyText: label('common.noData', 'No data') }}
                columns={[
                  { title: label('wpaPermissions.customerNo', 'Customer No'), dataIndex: 'customerNo', key: 'customerNo', width: 140 },
                  { title: label('wpaPermissions.branchNo', 'Branch No'), dataIndex: 'branchNo', key: 'branchNo', width: 120 },
                  { title: label('wpaPermissions.customerName', 'Name'), key: 'name', render: (_, r) => r.nameCn || r.nameEn || '-' },
                  {
                    title: label('common.actions', 'Actions'),
                    key: 'actions',
                    width: 80,
                    render: (_, record) => (
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeGroupEditCustomer(record.id)}
                      />
                    ),
                  },
                ]}
              />
            </div>
          )}
          {editingGroup?.id && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>{label('wpaPermissions.defaultCustomer', 'Default Customer')}</Text>
                <Button type="default" block style={{ marginTop: 8 }} onClick={openGroupEditDefaultTablePicker}>
                  {label('wpaPermissions.selectDefaultCustomerTable', '以表格選擇預設客戶')}
                </Button>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  {label(
                    'wpaPermissions.selectDefaultCustomerTableHint',
                    '須為群組客戶之一；若選擇尚未加入群組的客戶，將自動加入群組'
                  )}
                </Text>
              </div>
              <Table
                size="small"
                rowKey={(r) => r.customerId || r.id}
                dataSource={groupEditDefaultCustomer ? [groupEditDefaultCustomer] : []}
                pagination={false}
                locale={{ emptyText: label('common.noData', 'No data') }}
                columns={[
                  { title: label('wpaPermissions.customerNo', 'Customer No'), dataIndex: 'customerNo', key: 'customerNo', width: 140 },
                  { title: label('wpaPermissions.branchNo', 'Branch No'), dataIndex: 'branchNo', key: 'branchNo', width: 120 },
                  { title: label('wpaPermissions.customerName', 'Name'), key: 'name', render: (_, r) => r.nameCn || r.nameEn || '-' },
                  {
                    title: label('common.actions', 'Actions'),
                    key: 'actions',
                    width: 80,
                    render: () => (
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setGroupEditDefaultCustomer(null)} />
                    ),
                  },
                ]}
              />
            </div>
          )}
        </Form>
      </Modal>

      <CommerceCustomerTablePickerModal
        key={`group-edit-cust-picker-${editingGroup?.id || 'new'}-${groupEditCustomerPickerNonce}`}
        open={groupEditCustomerPickerOpen}
        onCancel={() => setGroupEditCustomerPickerOpen(false)}
        initialSelectedKeys={groupEditPickerInitialKeys}
        selectionMode={groupEditCustomerPickerMode === 'default' ? 'single' : 'multiple'}
        title={
          groupEditCustomerPickerMode === 'default'
            ? label('wpaPermissions.selectDefaultCustomer', 'Select default customer')
            : label('wpaPermissions.selectCustomersTable', '選擇客戶')
        }
        fetchWithAuth={fetchWithAuth}
        isTenantAdmin={isTenantAdmin}
        onApply={(payload) => {
          if (groupEditCustomerPickerMode === 'default') {
            applyGroupEditDefaultFromPicker(payload);
          } else {
            syncGroupEditCustomersFromPicker(payload);
          }
        }}
      />

      {/* Add member modal */}
      <Modal
        title={label('wpaPermissions.addMember', 'Add Member')}
        open={addMemberModalOpen}
        onCancel={() => {
          setAddMemberModalOpen(false);
          setMemberSearchSelectedIds([]);
        }}
        footer={
          <Space>
            <Button onClick={() => {
              setAddMemberModalOpen(false);
              setMemberSearchSelectedIds([]);
            }}>
              {label('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="primary"
              disabled={memberSearchSelectedIds.length === 0}
              onClick={() => {
                addMembers(memberSearchSelectedIds);
                setAddMemberModalOpen(false);
                setMemberSearchSelectedIds([]);
              }}
            >
              {label('common.add', 'Add')} ({memberSearchSelectedIds.length})
            </Button>
          </Space>
        }
        destroyOnClose
      >
        <Input.Search
          placeholder={label('wpaPermissions.searchAccount', 'Search PWA accounts...')}
          value={memberSearchText}
          onChange={(e) => setMemberSearchText(e.target.value)}
          onSearch={(v) => searchMembers(v)}
          loading={memberSearchLoading}
          enterButton
          style={{ marginBottom: 12 }}
        />
        <Table
          dataSource={memberSearchResults}
          rowKey="id"
          size="small"
          pagination={false}
          rowSelection={{
            selectedRowKeys: memberSearchSelectedIds,
            onChange: (keys) => setMemberSearchSelectedIds(keys),
          }}
          columns={[
            { title: label('wpaPermissions.loginId', 'Login ID'), dataIndex: 'loginId', key: 'loginId' },
            { title: label('wpaPermissions.name', 'Name'), dataIndex: 'name', key: 'name' },
            { title: label('wpaPermissions.phone', 'Phone'), dataIndex: 'phone', key: 'phone' },
            {
              title: '',
              key: 'action',
              width: 80,
              render: (_, record) => (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => { addMember(record.id); setAddMemberModalOpen(false); setMemberSearchSelectedIds([]); }}
                >
                  {label('common.add', 'Add')}
                </Button>
              ),
            },
          ]}
        />
      </Modal>

      <CommerceCustomerTablePickerModal
        key={`add-cust-picker-${addCustomerPickerNonce}`}
        open={addCustomerModalOpen}
        onCancel={() => setAddCustomerModalOpen(false)}
        initialSelectedKeys={expandedGroupCustomerPickerInitialKeys}
        selectionMode="multiple"
        title={label('wpaPermissions.addCustomer', 'Add Customer')}
        fetchWithAuth={fetchWithAuth}
        isTenantAdmin={isTenantAdmin}
        onApply={({ customerIds }) => {
          applyExpandedGroupCustomersPicker({ customerIds });
        }}
      />
    </div>
  );

  // ─── Render: Accounts Tab (取代原 Contacts tab) ─────────

  const accountColumns = [
    {
      title: label('wpaPermissions.loginId', 'Login ID'),
      dataIndex: 'loginId',
      key: 'loginId',
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: label('wpaPermissions.displayName', 'Display Name'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: label('wpaPermissions.phone', 'Phone'),
      dataIndex: 'phoneNo',
      key: 'phoneNo',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: label('wpaPermissions.email', 'Email'),
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: label('wpaPermissions.effectiveRole', 'Effective Role'),
      dataIndex: 'effectiveRole',
      key: 'effectiveRole',
      render: (v) => v ? <Tag color="blue">{v}</Tag> : '-',
    },
    {
      title: label('wpaPermissions.roleSource', 'Source'),
      dataIndex: 'roleSource',
      key: 'roleSource',
      render: (v) => {
        if (v === 'override') return <Tag color="orange">{label('wpaPermissions.accountOverride', 'Account')}</Tag>;
        if (v === 'group') return <Tag color="green">{label('wpaPermissions.group', 'Group')}</Tag>;
        return '-';
      },
    },
    {
      title: label('wpaPermissions.overrideCustomerCount', 'Assigned Customers'),
      dataIndex: 'overrideCustomerCount',
      key: 'overrideCustomerCount',
      width: 120,
      align: 'center',
      render: (v) => v ?? 0,
    },
    {
      title: label('wpaPermissions.groups', 'Groups'),
      dataIndex: 'groupCount',
      key: 'groupCount',
      width: 80,
      align: 'center',
      render: (v) => v ?? 0,
    },
    {
      title: label('wpaPermissions.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v) => (
        <Tag color={v === 'active' ? 'green' : 'default'}>
          {v || 'active'}
        </Tag>
      ),
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: label('common.actions', 'Actions'),
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditAccount(record)} />
          <Popconfirm
            title={label('wpaPermissions.deleteAccountConfirm', 'Delete this PWA account?')}
            onConfirm={() => handleDeleteAccount(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderWhatsappOtpPanel = () => {
    const activePreview = otpBodyText ? otpBodyText.replace(/\{\{1\}\}/g, '123456') : '';

    const statusColor = (s) => ({ APPROVED: 'green', REJECTED: 'red', PENDING: 'orange' }[s] || 'default');
    const statusLabel = (s) => ({ APPROVED: '已審批', REJECTED: '已拒絕', PENDING: '審核中' }[s] || s);
    const langLabel = (l) => ({ zh_TW: '繁中', zh_CN: '简中', en_US: 'EN', en: 'EN' }[l] || l || '—');

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => { loadOtpSettings(); loadOtpTemplates(); }}
            loading={otpSettingsLoading || otpTemplatesLoading}
          >
            {label('common.refresh', 'Refresh')}
          </Button>
        </div>

        <Space direction="vertical" style={{ width: '100%' }} size="large">

          {/* ══ A. 目前啟用中的 OTP Template ══ */}
          <Card
            size="small"
            title={<span>🟢 目前啟用的 OTP Template</span>}
            extra={otpTemplateId && (
              <Button size="small" danger onClick={() => { setOtpTemplateId(''); setOtpTemplateName(''); setOtpBodyText(''); }}>
                清除
              </Button>
            )}
          >
            <Spin spinning={otpSettingsLoading}>
              {otpTemplateId ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Template 名稱：</Text>
                    <Text strong style={{ fontFamily: 'monospace' }}>{otpTemplateName || otpTemplateId}</Text>
                  </div>
                  {otpBodyText && (
                    <div
                      style={{
                        background: '#e7f9f0', border: '1px solid #25d366', borderRadius: 8,
                        padding: '10px 14px', fontSize: 14, whiteSpace: 'pre-wrap',
                      }}
                    >
                      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                        {'效果預覽（{{1}} → 123456）'}
                      </Text>
                      {activePreview}
                    </div>
                  )}
                </Space>
              ) : (
                <Text type="secondary">尚未設定 OTP Template。請在下方建立或從列表選取已審批的 Template。</Text>
              )}
            </Spin>
          </Card>

          {/* ══ B. 建立新 OTP Template ══ */}
          <Card
            title={<span>📝 建立新 OTP Template</span>}
            extra={
              <Tag color="blue" style={{ fontSize: 11 }}>
                {'{{1}}'} 自動注入 OTP 驗證碼
              </Tag>
            }
          >
            <Form
              form={otpTplForm}
              layout="vertical"
              onFinish={createOtpTemplate}
              initialValues={{ category: 'AUTHENTICATION', language: 'zh_TW' }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="模板名稱"
                    rules={[
                      { required: true, message: '請輸入模板名稱' },
                      { pattern: /^[a-z0-9_]+$/, message: '只能使用小寫字母、數字和下劃線' },
                      { max: 187, message: '名稱最長 187 字元' },
                    ]}
                    help="只能使用小寫字母、數字和下劃線，名稱後系統會自動加公司識別碼後綴。"
                  >
                    <Input placeholder="例如：wpa_otp_code" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="language" label="語言" rules={[{ required: true }]}>
                    <Select>
                      <Select.Option value="zh_TW">繁體中文</Select.Option>
                      <Select.Option value="zh_CN">简体中文</Select.Option>
                      <Select.Option value="en_US">English</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* AUTHENTICATION 固定說明 */}
              <div style={{ background: '#f0f7ff', border: '1px solid #91caff', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                <Text style={{ fontSize: 13 }}>
                  <strong>🔒 AUTHENTICATION 模板</strong>　Meta 自動產生訊息文案，<strong>不允許自訂正文</strong>。<br />
                  系統將自動加入：
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12 }}>
                    <li>BODY：安全提醒（add_security_recommendation）</li>
                    <li>FOOTER：OTP 10 分鐘內有效</li>
                    <li>BUTTONS：COPY_CODE 一鍵複製按鈕</li>
                  </ul>
                </Text>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={otpTplCreating}
                style={{ minWidth: 160 }}
              >
                提交 Meta 審核
              </Button>
            </Form>
          </Card>

          {/* ══ C. 已建立的 OTP Templates 列表 ══ */}
          <Card
            title="已建立的 OTP Templates"
            extra={
              <Button size="small" icon={<ReloadOutlined />} loading={otpTemplatesLoading} onClick={loadOtpTemplates}>
                刷新
              </Button>
            }
          >
            <Spin spinning={otpTemplatesLoading}>
              {otpTemplates.length === 0 ? (
                <Text type="secondary">暫無 OTP Templates。建立後稍待 Meta 審核即可顯示。</Text>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {otpTemplates.map((tpl) => {
                    const body = tpl.components?.find((c) => c.type === 'BODY')?.text || '';
                    const tplCategory = (tpl.category || '').toUpperCase();
                    const tplLanguage = tpl.language || 'zh_TW';
                    const isActive = tpl.name === otpTemplateName || (tpl.id && tpl.id === otpTemplateId);
                    const isApproved = (tpl.status || '').toUpperCase() === 'APPROVED';
                    return (
                      <div
                        key={tpl.id || tpl.name}
                        style={{
                          border: isActive ? '2px solid #52c41a' : '1px solid #f0f0f0',
                          borderRadius: 8,
                          padding: '10px 14px',
                          background: isActive ? '#f6ffed' : '#fafafa',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Space size={6} wrap>
                              <Text strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{tpl.name}</Text>
                              <Tag color={statusColor(tpl.status?.toUpperCase())}>{statusLabel(tpl.status?.toUpperCase())}</Tag>
                              <Tag color="orange">{langLabel(tpl.language)}</Tag>
                              {tplCategory === 'AUTHENTICATION' && <Tag color="blue">AUTHENTICATION</Tag>}
                              {isActive && <Tag color="green">✓ 使用中</Tag>}
                            </Space>
                            {tplCategory === 'AUTHENTICATION' ? (
                              <div style={{ color: '#1890ff', fontSize: 12, marginTop: 4 }}>
                                Meta 自動產生 OTP 文案（COPY_CODE 按鈕）
                              </div>
                            ) : body ? (
                              <div style={{ color: '#555', fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {body}
                              </div>
                            ) : null}
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            {isApproved && !isActive && (
                              <Button
                                size="small"
                                type="primary"
                                loading={otpSettingsSaving}
                                onClick={async () => {
                                  setOtpTemplateId(tpl.id || tpl.name);
                                  setOtpTemplateName(tpl.name);
                                  setOtpBodyText(body);
                                  setOtpTemplateCategory(tplCategory || 'AUTHENTICATION');
                                  setOtpTemplateLanguage(tplLanguage);
                                  await saveOtpSettings(tpl.name, tplLanguage, tplCategory || 'AUTHENTICATION', body);
                                }}
                              >
                                設為使用中
                              </Button>
                            )}
                            {isActive && (
                              <Button
                                size="small"
                                type="default"
                                loading={otpSettingsSaving}
                                onClick={() => saveOtpSettings()}
                              >
                                重新儲存
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Space>
              )}
            </Spin>
          </Card>

        </Space>
      </div>
    );
  };

  const renderEmailOtpPanel = () => {
    const emailPreviewSubject = (otpEmailSubject || '').replace(/\{\{1\}\}/g, '123456');
    const emailPreviewBody = (otpEmailBody || '').replace(/\{\{1\}\}/g, '123456');

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {label(
              'wpaPermissions.otpEmailHint',
              '用於 PWA「忘記密碼」電郵驗證碼。主旨或正文須含 {{1}}，發送時會替換為 6 位 OTP。'
            )}
          </Text>
          <Button size="small" icon={<ReloadOutlined />} onClick={loadOtpSettings} loading={otpSettingsLoading}>
            {label('common.refresh', 'Refresh')}
          </Button>
        </div>

        <Spin spinning={otpSettingsLoading}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card
              title={label('wpaPermissions.otpEmailTemplateTitle', '電郵 OTP 模板')}
              extra={
                <Tag color="blue" style={{ fontSize: 11 }}>
                  {'{{1}}'} → OTP
                </Tag>
              }
            >
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {label('wpaPermissions.otpEmailSubject', '郵件主旨')}
                </Text>
                <Input
                  value={otpEmailSubject}
                  onChange={(e) => setOtpEmailSubject(e.target.value)}
                  placeholder={label('wpaPermissions.otpEmailSubjectPlaceholder', '例如：【公司名稱】密碼重設驗證碼 {{1}}')}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  {label('wpaPermissions.otpEmailBody', '郵件正文（支援 HTML）')}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                  {label('wpaPermissions.otpEmailBodyRteHint', '工具列可設定粗體、連結等；右側按鈕可插入 OTP 驗證碼變量。')}
                </Text>
                <RichTextEditor
                  value={otpEmailBody}
                  onChange={setOtpEmailBody}
                  height={300}
                  placeholder={label(
                    'wpaPermissions.otpEmailBodyPlaceholder',
                    '<p>您好，</p><p>您的驗證碼為：<strong>{{1}}</strong></p><p>驗證碼 5 分鐘內有效。</p>'
                  )}
                  customToolbarButtons={[
                    { key: 'otp-code', icon: '{{1}}', insertText: '{{1}}', title: label('wpaPermissions.otpInsertCode', '插入 OTP 驗證碼') },
                  ]}
                />
              </div>
              {(otpEmailSubject || otpEmailBody) && (
                <div
                  style={{
                    background: '#f0f7ff',
                    border: '1px solid #91caff',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 16,
                  }}
                >
                  <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 8 }}>
                    {label('wpaPermissions.otpEmailPreview', '效果預覽（{{1}} → 123456）')}
                  </Text>
                  {otpEmailSubject ? (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Subject: </Text>
                      <Text>{emailPreviewSubject}</Text>
                    </div>
                  ) : null}
                  {otpEmailBody ? (
                    <div
                      style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}
                      dangerouslySetInnerHTML={{ __html: emailPreviewBody }}
                    />
                  ) : null}
                </div>
              )}
              <Button type="primary" loading={otpEmailSaving} onClick={saveEmailOtpSettings}>
                {label('common.save', 'Save')}
              </Button>
            </Card>
          </Space>
        </Spin>
      </div>
    );
  };

  const renderOtpSettingsTab = () => (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>
          {label('wpaPermissions.otpSettingsTitle', 'OTP 設定')}
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => { loadOtpSettings(); if (otpSubTab === 'whatsapp') loadOtpTemplates(); }}
          loading={otpSettingsLoading || (otpSubTab === 'whatsapp' && otpTemplatesLoading)}
        >
          {label('common.refresh', 'Refresh')}
        </Button>
      </div>
      <Tabs
        size="small"
        activeKey={otpSubTab}
        onChange={setOtpSubTab}
        items={[
          {
            key: 'whatsapp',
            label: label('wpaPermissions.otpTabWhatsapp', 'WhatsApp'),
            children: renderWhatsappOtpPanel(),
          },
          {
            key: 'email',
            label: label('wpaPermissions.otpTabEmail', '電郵模板'),
            children: renderEmailOtpPanel(),
          },
        ]}
      />
    </div>
  );

  const renderWelcomeSettingsTab = () => (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>{label('wpaPermissions.welcomeSettingsTitle', 'Welcome Message')}</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => { loadWelcomeSettings(); if (welcomeSubTab === 'whatsapp') loadWelcomeTemplates(); }}
          loading={welcomeSettingsLoading || (welcomeSubTab === 'whatsapp' && welcomeTemplatesLoading)}
        >
          {label('common.refresh', 'Refresh')}
        </Button>
      </div>
      <Tabs
        size="small"
        activeKey={welcomeSubTab}
        onChange={setWelcomeSubTab}
        items={[
          {
            key: 'whatsapp',
            label: label('wpaPermissions.otpTabWhatsapp', 'WhatsApp'),
            children: (
              <CommerceWpaWelcomeWhatsappPanel
                label={label}
                welcomeSettingsLoading={welcomeSettingsLoading}
                welcomeTemplateName={welcomeTemplateName}
                welcomeTemplateCategory={welcomeTemplateCategory}
                welcomeBodyText={welcomeBodyText}
                welcomeTplForm={welcomeTplForm}
                welcomeTplCreating={welcomeTplCreating}
                createWelcomeTemplate={createWelcomeTemplate}
                welcomeTemplates={welcomeTemplates}
                welcomeTemplatesLoading={welcomeTemplatesLoading}
                loadWelcomeTemplates={loadWelcomeTemplates}
                welcomeSettingsSaving={welcomeSettingsSaving}
                saveWelcomeWhatsappSettings={saveWelcomeWhatsappSettings}
                setWelcomeTemplateName={setWelcomeTemplateName}
                setWelcomeBodyText={setWelcomeBodyText}
                setWelcomeTemplateCategory={setWelcomeTemplateCategory}
                setWelcomeTemplateLanguage={setWelcomeTemplateLanguage}
              />
            ),
          },
          {
            key: 'email',
            label: label('wpaPermissions.otpTabEmail', '電郵模板'),
            children: (
              <CommerceWpaWelcomeEmailPanel
                label={label}
                welcomeSettingsLoading={welcomeSettingsLoading}
                welcomeEmailSubject={welcomeEmailSubject}
                setWelcomeEmailSubject={setWelcomeEmailSubject}
                welcomeEmailBody={welcomeEmailBody}
                setWelcomeEmailBody={setWelcomeEmailBody}
                welcomeEmailSaving={welcomeEmailSaving}
                saveWelcomeEmailSettings={saveWelcomeEmailSettings}
              />
            ),
          },
        ]}
      />
    </div>
  );

  const renderAccountsTab = () => (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>
          {label('wpaPermissions.accountsTitle', 'PWA Accounts')}
        </Title>
        <Space>
          <Input.Search
            placeholder={label('wpaPermissions.searchAccount', 'Search PWA accounts...')}
            value={accountSearch}
            onChange={(e) => setAccountSearch(e.target.value)}
            onSearch={(v) => { setAccountPage(1); loadAccounts(1, accountPageSize, v); }}
            enterButton={<SearchOutlined />}
            style={{ width: 300 }}
            allowClear
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadAccounts(accountPage, accountPageSize, accountSearch)}
            loading={accountsLoading}
          >
            {label('common.refresh', 'Refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateAccount}>
            {label('wpaPermissions.createAccount', 'Create Account')}
          </Button>
        </Space>
      </div>

      <Table
        dataSource={accounts}
        columns={accountColumns}
        rowKey="id"
        loading={accountsLoading}
        pagination={false}
        size="middle"
      />
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Pagination
          current={accountPage}
          pageSize={accountPageSize}
          total={accountTotal}
          showSizeChanger
          pageSizeOptions={[10, 20, 50, 100]}
          showTotal={(total) => `${total} ${label('wpaPermissions.items', 'items')}`}
          onChange={(page, size) => {
            setAccountPage(page);
            setAccountPageSize(size);
            loadAccounts(page, size, accountSearch);
          }}
        />
      </div>

      {/* Account create / edit modal */}
      <Modal
        title={editingAccount?.id
          ? `${label('wpaPermissions.editAccountFor', 'Edit Account')} — ${editingAccount?.loginId || ''}`
          : label('wpaPermissions.createAccount', 'Create Account')}
        open={accountModalOpen}
        onCancel={() => setAccountModalOpen(false)}
        footer={
          <Space>
            {editingAccount?.id && (
              <Popconfirm
                title={label('wpaPermissions.deleteAccountConfirm', 'Delete this PWA account?')}
                onConfirm={() => handleDeleteAccount(editingAccount.id)}
              >
                <Button danger loading={accountSaving}>
                  {label('wpaPermissions.deleteAccount', 'Delete Account')}
                </Button>
              </Popconfirm>
            )}
            <Button onClick={() => setAccountModalOpen(false)}>
              {label('common.cancel', 'Cancel')}
            </Button>
            {editingAccount?.id && (
              <Popconfirm
                title={label(
                  'wpaPermissions.saveAndResendPasswordConfirm',
                  '將儲存變更、自動產生新密碼並重發 welcome 訊息（含電郵）。確定繼續？'
                )}
                onConfirm={() => handleSaveAccount({ resendWelcomeWithNewPassword: true })}
              >
                <Button loading={accountSaving}>
                  {label('wpaPermissions.saveAndResendPassword', '儲存並重發密碼')}
                </Button>
              </Popconfirm>
            )}
            <Button type="primary" onClick={() => handleSaveAccount()} loading={accountSaving}>
              {editingAccount?.id
                ? label('common.save', 'Save')
                : label('wpaPermissions.saveAndSendNotification', '儲存並發出通知')}
            </Button>
          </Space>
        }
        destroyOnClose
        width={720}
      >
        {accountLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text type="secondary">{label('common.loading', 'Loading...')}</Text>
          </div>
        ) : (
          <Form form={accountForm} layout="vertical">
            <Tabs
              activeKey={accountSettingsTab}
              onChange={setAccountSettingsTab}
              items={[
                {
                  key: 'identity',
                  label: label('wpaPermissions.accountTabIdentity', '帳號／角色'),
                  children: (
                    <div style={{ paddingTop: 8 }}>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                        {label(
                          'wpaPermissions.accountIdentityHint',
                          'PWA 登入帳號（與聯絡人主檔解耦）。Phone 用於 WhatsApp OTP 登入比對。'
                        )}
                      </Text>
                      <Form.Item
                        name="loginId"
                        label={label('wpaPermissions.loginIdField', '登入帳號')}
                        rules={[{ required: true, message: label('wpaPermissions.loginIdRequired', '請填寫登入帳號') }]}
                      >
                        <Input autoComplete="username" />
                      </Form.Item>
                      <Form.Item name="displayName" label={label('wpaPermissions.displayName', 'Display Name')}>
                        <Input />
                      </Form.Item>
                      <Form.Item name="phoneNo" label={label('wpaPermissions.phone', 'Phone')}>
                        <Input placeholder={label('wpaPermissions.phonePlaceholder', '例如 85291234567（不含 +）')} />
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label={label('wpaPermissions.email', 'Email')}
                        rules={[{ type: 'email', message: label('wpaPermissions.emailInvalid', '請輸入有效的電郵地址') }]}
                      >
                        <Input placeholder="example@company.com" autoComplete="email" inputMode="email" />
                      </Form.Item>
                      {!editingAccount?.id ? (
                        <PasswordFieldWithGenerate
                          form={accountForm}
                          scope={PASSWORD_POLICY_SCOPES.wpaAccount}
                          policy={wpaPasswordPolicy}
                          generating={wpaPasswordGenerating}
                          passwordRequired
                          showConfirm={false}
                          useGridLayout={false}
                          passwordLabel={label('wpaPermissions.password', '密碼')}
                          passwordRequiredMessage={label('wpaPermissions.passwordRequiredForCreate', '建立帳號須設定密碼')}
                          generateLabel={label('common.passwordPolicy.generatePassword', '自動產生')}
                        />
                      ) : (
                        <PasswordFieldWithGenerate
                          form={accountForm}
                          scope={PASSWORD_POLICY_SCOPES.wpaAccount}
                          policy={wpaPasswordPolicy}
                          generating={wpaPasswordGenerating}
                          passwordName="newPassword"
                          skipPasswordValidationWhenEmpty
                          showConfirm={false}
                          useGridLayout={false}
                          passwordLabel={label('wpaPermissions.newPassword', '新密碼')}
                          passwordPlaceholder={label('wpaPermissions.newPasswordHint', '留空則不變更密碼')}
                          generateLabel={label('common.passwordPolicy.generatePassword', '自動產生')}
                        />
                      )}
                      <Form.Item name="status" label={label('wpaPermissions.status', 'Status')}>
                        <Select>
                          <Select.Option value="active">{label('wpaPermissions.active', 'Active')}</Select.Option>
                          <Select.Option value="disabled">{label('wpaPermissions.inactive', 'Inactive')}</Select.Option>
                        </Select>
                      </Form.Item>
                      <Divider plain style={{ margin: '16px 0' }} />
                      <Form.Item name="roleId" label={label('wpaPermissions.role', 'Role')}>
                        <Select
                          allowClear
                          placeholder={label('wpaPermissions.selectRole', 'Select a role')}
                        >
                          {roles.map((r) => (
                            <Select.Option key={r.id} value={r.id}>
                              {r.displayName || r.roleKey}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  ),
                },
                {
                  key: 'customers',
                  label: label('wpaPermissions.accountTabCustomers', '客戶／預設客戶'),
                  children: (
                    <div style={{ paddingTop: 8 }}>
                      <Form.Item label={label('wpaPermissions.customers', 'Customers')}>
                        <div>
                          <div style={{ marginBottom: 8 }}>
                            <Button type="default" onClick={openAccountCustomerPicker} block>
                              {`${label('wpaPermissions.selectCustomersTable', '以表格選擇客戶')} (${(watchedAccountCustomerIds || []).length})`}
                            </Button>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                              {label(
                                'wpaPermissions.selectCustomersTableHint',
                                '開啟與客戶主檔相同的列表，可搜尋、篩選公司（租戶管理員）、多選'
                              )}
                            </Text>
                          </div>
                          <Table
                            size="small"
                            rowKey="rowKey"
                            dataSource={accountCustomersTableRows}
                            pagination={false}
                            locale={{ emptyText: label('common.noData', 'No data') }}
                            columns={[
                              { title: label('wpaPermissions.customerNo', 'Customer No'), dataIndex: 'customerNo', key: 'customerNo', width: 140 },
                              { title: label('wpaPermissions.branchNo', 'Branch No'), dataIndex: 'branchNo', key: 'branchNo', width: 120 },
                              { title: label('wpaPermissions.customerName', 'Name'), key: 'name', render: (_, r) => r.nameCn || r.nameEn || '-' },
                              {
                                title: label('common.actions', 'Actions'),
                                key: 'actions',
                                width: 80,
                                render: (_, record) => (
                                  <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeAccountCustomerRow(record.customerId)}
                                  />
                                ),
                              },
                            ]}
                          />
                          <Form.Item name="customerIds" noStyle initialValue={[]}>
                            <NoOpFormControl />
                          </Form.Item>
                        </div>
                      </Form.Item>
                      <Form.Item label={label('wpaPermissions.defaultCustomer', 'Default Customer')}>
                        <div>
                          <div style={{ marginBottom: 8 }}>
                            <Button type="default" block style={{ marginTop: 0 }} onClick={openAccountDefaultTablePicker}>
                              {label('wpaPermissions.selectDefaultCustomerTable', '以表格選擇預設客戶')}
                            </Button>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                              {label(
                                'wpaPermissions.selectDefaultCustomerOverrideHint',
                                '須為已指派客戶之一；若選擇尚未指派的客戶，將一併加入指派清單'
                              )}
                            </Text>
                          </div>
                          <Table
                            size="small"
                            rowKey="customerId"
                            dataSource={accountDefaultTableRow ? [accountDefaultTableRow] : []}
                            pagination={false}
                            locale={{ emptyText: label('common.noData', 'No data') }}
                            columns={[
                              { title: label('wpaPermissions.customerNo', 'Customer No'), dataIndex: 'customerNo', key: 'customerNo', width: 140 },
                              { title: label('wpaPermissions.branchNo', 'Branch No'), dataIndex: 'branchNo', key: 'branchNo', width: 120 },
                              { title: label('wpaPermissions.customerName', 'Name'), key: 'name', render: (_, r) => r.nameCn || r.nameEn || '-' },
                              {
                                title: label('common.actions', 'Actions'),
                                key: 'actions',
                                width: 80,
                                render: () => (
                                  <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => accountForm.setFieldsValue({ defaultCustomerId: undefined })}
                                  />
                                ),
                              },
                            ]}
                          />
                          <Form.Item name="defaultCustomerId" noStyle>
                            <NoOpFormControl />
                          </Form.Item>
                        </div>
                      </Form.Item>
                    </div>
                  ),
                },
              ]}
            />
          </Form>
        )}
      </Modal>

      <CommerceCustomerTablePickerModal
        key={`acct-cust-picker-${editingAccount?.id || 'new'}-${accountCustomerPickerNonce}`}
        open={accountCustomerPickerOpen}
        onCancel={() => setAccountCustomerPickerOpen(false)}
        initialSelectedKeys={accountPickerInitialKeys}
        selectionMode="multiple"
        title={label('wpaPermissions.selectCustomersTable', '選擇客戶')}
        fetchWithAuth={fetchWithAuth}
        isTenantAdmin={isTenantAdmin}
        onApply={handleAccountCustomerPickerApply}
      />
      <CommerceCustomerTablePickerModal
        key={`acct-default-picker-${editingAccount?.id || 'new'}-${accountDefaultPickerNonce}`}
        open={accountDefaultCustomerPickerOpen}
        onCancel={() => setAccountDefaultCustomerPickerOpen(false)}
        initialSelectedKeys={accountDefaultPickerInitialKeys}
        selectionMode="single"
        title={label('wpaPermissions.selectDefaultCustomer', 'Select default customer')}
        fetchWithAuth={fetchWithAuth}
        isTenantAdmin={isTenantAdmin}
        onApply={handleAccountDefaultPickerApply}
      />
    </div>
  );

  // ─── Main render ────────────────────────────────────────

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          {label('wpaPermissions.pageTitle', 'PWA Permission Management')}
        </Title>
        {isTenantAdmin && (
          <Select
            allowClear
            placeholder={label('wpaPermissions.selectCompany', '選擇公司')}
            style={{ minWidth: 220 }}
            options={companiesForSelect.map((c) => ({ value: c.id, label: c.name }))}
            value={companyFilter || undefined}
            onChange={(v) => setCompanyFilter(v ?? null)}
          />
        )}
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'roles',
            label: label('wpaPermissions.tabRoles', 'Roles'),
            children: renderRolesTab(),
          },
          {
            key: 'groups',
            label: label('wpaPermissions.tabGroups', 'Groups'),
            children: renderGroupsTab(),
          },
          {
            key: 'accounts',
            label: label('wpaPermissions.tabAccounts', 'PWA Accounts'),
            children: renderAccountsTab(),
          },
          {
            key: 'otp-settings',
            label: label('wpaPermissions.tabOtpSettings', 'OTP'),
            children: renderOtpSettingsTab(),
          },
          {
            key: 'welcome-settings',
            label: label('wpaPermissions.tabWelcomeSettings', 'Welcome Message'),
            children: renderWelcomeSettingsTab(),
          },
        ]}
      />
    </div>
  );
};

export default CommerceWpaPermissionPage;
